// =============================================================================
// SEVRIC Chat — Supabase client (native Firebase Auth integration)
// -----------------------------------------------------------------------------
// We use Supabase's built-in Firebase third-party auth: the user's Firebase
// ID token is sent directly to Supabase, which verifies it against Google's
// JWKS and exposes the custom claims (tenantId, apps, etc.) to RLS via
// `auth.jwt()`. No bridge function, no custom JWT signing, no JWKS hosting.
//
// Configure once in Supabase Dashboard → Authentication → Sign In Providers
// → Third-Party Auth → Firebase → enter project id `sevric-portal`.
//
// Token lifecycle: Firebase ID tokens are good for 1 hour. We attach a
// listener via `onIdTokenChanged` so every refresh (Firebase rotates them
// automatically on a ~55-minute cadence) updates the Supabase session
// without us having to schedule our own refresh timer.
// =============================================================================
import { createClient } from "@supabase/supabase-js";
import { getAuth, onIdTokenChanged } from "firebase/auth";

const SUPABASE_URL  = import.meta.env.VITE_SUPABASE_URL  || "";
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

if (!SUPABASE_URL || !SUPABASE_ANON) {
  // eslint-disable-next-line no-console
  console.warn("[SEVRIC Chat] Supabase env vars missing — set VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY.");
}

let _client = null;
let _firebaseApp = null;
let _unsubscribeIdToken = null;

export function getSupabase() {
  if (_client) return _client;
  _client = createClient(SUPABASE_URL, SUPABASE_ANON, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    realtime: { params: { eventsPerSecond: 10 } },
    global: { headers: { "x-sevric-app": "chat" } },
  });
  return _client;
}

// -----------------------------------------------------------------------------
// connectChat(firebaseApp) — bootstrap the Supabase session from the current
// Firebase user. Subscribes to ID-token changes so refreshes propagate
// automatically.
// -----------------------------------------------------------------------------
export async function connectChat(firebaseApp) {
  _firebaseApp = firebaseApp;
  const supa = getSupabase();
  const auth = getAuth(firebaseApp);
  const user = auth.currentUser;
  if (!user) {
    throw new Error("You're signed out. Sign in again to use messaging.");
  }
  const token = await user.getIdToken();
  await applyToken(token);

  // Stay in sync with Firebase's automatic ~55-min token refresh. Every
  // refresh re-publishes the new token to Supabase realtime + REST.
  if (_unsubscribeIdToken) _unsubscribeIdToken();
  _unsubscribeIdToken = onIdTokenChanged(auth, async (nextUser) => {
    if (!nextUser) return;
    try {
      const fresh = await nextUser.getIdToken();
      await applyToken(fresh);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[SEVRIC Chat] token refresh failed:", err?.message);
    }
  });

  // Decode the tenant out of the token so the caller has something useful.
  // We don't verify the signature on the client — Supabase does that.
  const payload = decodeJwtPayload(token);
  return {
    token,
    expiresIn: payload?.exp ? Math.max(0, payload.exp - Math.floor(Date.now() / 1000)) : 3600,
    tenantId: payload?.tenantId || null,
    uid: payload?.user_id || payload?.sub || user.uid,
  };
}

async function applyToken(token) {
  const supa = getSupabase();
  await supa.realtime.setAuth(token);
  await supa.auth.setSession({
    access_token: token,
    refresh_token: "noop",  // Firebase rotates; we don't use Supabase refresh
  });
}

function decodeJwtPayload(token) {
  try {
    const [, payloadB64] = token.split(".");
    const padded = payloadB64.padEnd(payloadB64.length + ((4 - (payloadB64.length % 4)) % 4), "=")
      .replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(padded));
  } catch { return null; }
}

export function disconnectChat() {
  if (_unsubscribeIdToken) { _unsubscribeIdToken(); _unsubscribeIdToken = null; }
  if (_client) {
    _client.auth.signOut().catch(() => {});
    _client = null;
  }
  _firebaseApp = null;
}
